// Setup › Recall: "Standing rules" (RECALL-RULES) and "Memory upkeep" (RECALL-UPKEEP).
// Standing rules: the company's rules (every agent), each with its Recall state, a hover lift icon
// for owners and admins, and "Waiting for approval" on a proposal; the DE-specific rules as one
// line per DE linking to its page. Memory upkeep: Recall's dreams (nightly tidy-up of every space),
// the hour they run (UTC), whether topic tidying applies without a person, and the hot copy; saved
// in `settings.memory` with `PATCH companies/{co}` like the agent memory writes switch. Off by
// default; the dream schedule's Recall state comes from `recall.upkeep`.
import type { OperationOutput } from "@companyos/sdk";
import {
  NativeSelect,
  NativeSelectOption,
  SettingsGroup,
  SettingsRow,
  Switch,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isManager } from "../company";
import { companyQuery, keys } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import {
  AddRuleButton,
  type MemoryRule,
  memoryRulesQuery,
  RecallStateBadge,
  RuleRow,
  useRuleModals,
} from "./rules";

export type RecallUpkeep = OperationOutput<"recall.upkeep">;

export const upkeepKey = (co: string) => ["company", co, "memory", "recall-upkeep"] as const;

export const recallUpkeepQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: upkeepKey(co),
    queryFn: () => api.client.recall.upkeep({ params: { co } }),
  });

export function StandingRules({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const q = useQuery(memoryRulesQuery(api, co));
  const canManage = q.data?.can_manage;
  const modals = useRuleModals(api, co, canManage);
  if (q.isPending) return <Loading label="Loading standing rules" rows={2} />;
  if (q.isError) return <LoadError what="standing rules" error={q.error} />;
  const company_ = q.data.data.filter((r) => r.scope === "company");
  const byDe = new Map<string, { name: string; rules: MemoryRule[] }>();
  for (const r of q.data.data) {
    if (r.scope !== "de" || !r.de) continue;
    const g = byDe.get(r.de.membership_id) ?? { name: r.de.name, rules: [] };
    g.rules.push(r);
    byDe.set(r.de.membership_id, g);
  }
  return (
    <SettingsGroup
      title="Standing rules"
      count={company_.filter((r) => r.status === "active").length}
      data-slot="recall-rules"
      actions={<AddRuleButton onClick={() => modals.add({ scope: "company", source: "setup" })} />}
    >
      {modals.dialogs}
      {company_.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">No company rules yet.</p>
      ) : (
        <ul className="flex flex-col px-4 py-1.5" aria-label="Company rules">
          {company_.map((r) => (
            <RuleRow
              key={r.id}
              company={company}
              rule={r}
              canLift={canManage === true}
              onLift={modals.lift}
            />
          ))}
        </ul>
      )}
      {byDe.size ? (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-subtle px-4 py-2 text-xs text-muted-foreground"
          data-slot="recall-de-rules"
        >
          <span>DE-specific</span>
          {[...byDe.entries()].map(([id, g]) => (
            <Link
              key={id}
              to="/c/$slug/agents/de/$id"
              params={{ slug: company.slug, id }}
              search={keepFocus}
              className="inline-flex items-center gap-1 text-foreground underline-offset-4 hover:underline"
            >
              <WorkerBadge kind="de" size="sm" />
              {workerName(g.name)}
              <span className="text-faint tabular-nums">{g.rules.length}</span>
            </Link>
          ))}
        </div>
      ) : null}
    </SettingsGroup>
  );
}

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hh = (h: number) => `${String(h).padStart(2, "0")}:00 UTC`;

type Dreams = { enabled: boolean; hour_utc: number; auto_apply_topics: boolean };

export function MemoryUpkeep({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const q = useQuery(companyQuery(api, co));
  const upkeep = useQuery(recallUpkeepQuery(api, co));
  const canEdit = isManager(company);
  const save = useMutation({
    mutationFn: (next: { dreams?: Dreams; hot_copy?: { enabled: boolean } }) => {
      const settings = q.data?.settings ?? {};
      return api.client.companies.update({
        params: { co },
        body: { settings: { ...settings, memory: { ...settings.memory, ...next } } },
      });
    },
    onSuccess: (detail) => {
      qc.setQueryData(keys.company(co), detail);
      void qc.invalidateQueries({ queryKey: upkeepKey(co) });
    },
  });
  if (q.isPending) return <Loading label="Loading memory upkeep" rows={2} />;
  if (q.isError) return <LoadError what="memory upkeep" error={q.error} />;
  const m = q.data.settings.memory;
  const dreams: Dreams = {
    enabled: m?.dreams?.enabled === true,
    hour_utc: m?.dreams?.hour_utc ?? 2,
    auto_apply_topics: m?.dreams?.auto_apply_topics === true,
  };
  const hot = m?.hot_copy?.enabled === true;
  const d = upkeep.data?.dreams;
  const busy = save.isPending || !canEdit;
  const setDreams = (patch: Partial<Dreams>) => save.mutate({ dreams: { ...dreams, ...patch } });
  return (
    <SettingsGroup
      title="Memory upkeep"
      data-slot="recall-upkeep"
      actions={
        d ? (
          <RecallStateBadge
            state={d.recall_state}
            detail={d.recall_detail}
            note="Whether Recall took the dream schedule."
          />
        ) : null
      }
    >
      <SettingsRow label="Dreams" hint="Nightly tidy-up of memory">
        {dreams.enabled ? (
          <NativeSelect
            size="sm"
            aria-label="Dream hour (UTC)"
            value={String(dreams.hour_utc)}
            disabled={busy}
            onChange={(e) => setDreams({ hour_utc: Number(e.target.value) })}
          >
            {HOURS.map((h) => (
              <NativeSelectOption key={h} value={String(h)}>
                {hh(h)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        ) : null}
        <Switch
          aria-label="Dreams"
          checked={dreams.enabled}
          disabled={busy}
          onCheckedChange={(v) => setDreams({ enabled: v })}
        />
      </SettingsRow>
      {dreams.enabled ? (
        <SettingsRow label="Auto-apply topic tidying" hint="Everything else waits for review">
          <Switch
            aria-label="Auto-apply topic tidying"
            checked={dreams.auto_apply_topics}
            disabled={busy}
            onCheckedChange={(v) => setDreams({ auto_apply_topics: v })}
          />
        </SettingsRow>
      ) : null}
      <SettingsRow label="Hot copy" hint="Agents keep working while Recall is down">
        <Switch
          aria-label="Hot copy"
          checked={hot}
          disabled={busy}
          onCheckedChange={(v) => save.mutate({ hot_copy: { enabled: v } })}
        />
      </SettingsRow>
      {save.isError ? (
        <p role="alert" className="px-4 py-2 text-[13px] text-destructive">
          {problemText(save.error, "The setting was not saved. Try again.")}
        </p>
      ) : null}
    </SettingsGroup>
  );
}
